import Rendable from "@/glxp/abstract/rendable"
import Shader from "@/glxp/utils/shader"
import Node from "@/glxp/abstract/node"
import DebugController from '@/glxp/debug/debugController'
import GEOM from "@/glxp/geom/plane"
import ShaderManifest from "../shaderManifest"
import { vec2, vec3, mat4 } from 'gl-matrix'
import RAF from '@/js/utils/raf'


let _NV = mat4.create()


class Mesh extends Rendable {
  constructor(scene, geom, shader, texture) {
    super(scene)

    this.gl = scene.gl
    this.scene = scene
    this.geom = geom
    this.name = geom.name

    this.node = new Node()
    this.shader = new Shader(ShaderManifest[shader])
    this.texture = texture
    this.active = true
    this.depthTest = true
    this.section = "Scene_1"

    this.node.position  = vec3.clone(geom.translate)
    this.node.scale     = vec3.clone(geom.scale)
    this.node.rotation  = vec3.clone(geom.rotation)

    this.progress = 0
    this.time = 0

    this.hasNormal = true
    this.initBuffer({
      vertices: geom.vertices,
      normal: geom.normal,
      uvs: geom.uvs,
      indices: geom.indices,
    })

    this.initProgram(this.shader.vert, this.shader.frag)
    this.initVao()
    this.initMatrix()
    this.createUniforms()
    this.initDebug()
  }

  initDebug(){
    this.debugConfig = {}
    this.debugConfig['radius'] = { value: 0.1, range: [0, 5] }
    this.debugConfig['bluriness'] = { value: 5, range: [0, 5] }
    this.debugConfig['progress'] = { value: 0, range: [0, 1] }
    
    DebugController.addConfig(this.debugConfig, 'circle')
  }


  createUniforms() {
    this.createUniform('uNMatrix', 'mat4')
    this.createUniform("uTexture", "texture")
    this.createUniform("uGrain", "texture")
    this.createUniform("uReflect", "texture")
    this.createUniform("uNoise", "texture")

    this.createUniform("uRadius")
    this.createUniform("uBlur")
    this.createUniform("uProgress")
    this.createUniform("uGrainValue")
    this.createUniform("uGrainScale")
  

    this.createUniform("uTime")
  }

  applyState() {
    let gl = this.gl
    this.scene.applyDefaultState()
  }

  preRender() {
    this.time += RAF.dt / 1000
  }

  render() {

    if (!this.active) {
      return
    }
    this.preRender()

    let gl = this.gl
    gl.useProgram(this.program)
    gl.bindVertexArray(this.vao)
    this.applyState()

    mat4.mul(_NV, this.scene.camera.getViewMatrix(), this.node.getMatrix())
    mat4.invert(_NV, _NV)
    mat4.transpose(_NV, _NV)
    this.bindUniform('uNMatrix', _NV)

    if (this.texture) {
      gl.activeTexture(gl.TEXTURE0)
      gl.bindTexture(
        gl.TEXTURE_2D,
        this.scene.textureLoader.getTexture(this.texture)
      )
      this.bindUniform("uTexture", 0)
    }

    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(
      gl.TEXTURE_2D,
      this.scene.textureLoader.getTexture("Grain")
    )
    this.bindUniform('uGrain', 1)

    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(
      gl.TEXTURE_2D,
      this.scene.textureLoader.getTexture("Water_Reflections")
    )
    this.bindUniform('uReflect', 2)

    gl.activeTexture(gl.TEXTURE3)
    gl.bindTexture(
      gl.TEXTURE_2D,
      this.scene.textureLoader.getTexture("WaterNoise")
    )
    this.bindUniform('uNoise', 3)

    this.bindUniform('uRadius', DebugController.configs.circle.radius.value)
    this.bindUniform('uBlur',  DebugController.configs.circle.bluriness.value)
    this.bindUniform('uProgress',  this.progress)
    this.bindUniform('uGrainValue', this.scene.config.grain.value)
    this.bindUniform('uGrainScale', this.scene.config.grainScaleWater.value)
   
    this.bindUniform('uTime', this.time * 0.66)
    this.bindMatrixUniforms(this.scene.camera)


    gl.drawElements(
      gl.TRIANGLES,
      this.geom.indices.length,
      gl.UNSIGNED_SHORT,
      0
    )
    gl.bindVertexArray(null)
  }
}

export default Mesh
